/**
 * API client for the GHASC Literature backend.
 *
 * Handles three things automatically:
 *   1. Attaches the JWT to every request from localStorage.
 *   2. Redirects to /login on 401 (token expired or invalidated server-side),
 *      EXCEPT for login endpoints themselves — those handle their own errors.
 *   3. Parses JSON responses and returns the parsed object.
 *
 * Pattern lifted from the timesheets app's services/api.js, kept consistent
 * so anyone familiar with that codebase recognizes this one immediately.
 */

const API_BASE = ''   // empty in dev (proxied), empty in prod (same origin)

class ApiService {
    constructor() {
        this._redirecting = false
    }

    getToken() {
        const stored = localStorage.getItem('ghasc_user')
        if (!stored) return null
        try {
            return JSON.parse(stored).token
        } catch {
            return null
        }
    }

    /**
     * Called when any non-login endpoint returns 401. Clears local auth and
     * bounces to /login. Guards against redirect loops.
     */
    handleUnauthorized() {
        if (this._redirecting) return
        if (typeof window !== 'undefined' && window.location.pathname === '/login') return

        this._redirecting = true
        sessionStorage.setItem('ghasc_auth_message', 'Your session expired. Please log in again.')
        localStorage.removeItem('ghasc_user')
        setTimeout(() => { window.location.href = '/login' }, 50)
    }

    async request(method, endpoint, data = null, options = {}) {
        const url = `${API_BASE}${endpoint}`
        const token = this.getToken()

        const config = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
            config.body = JSON.stringify(data)
        }

        try {
            const response = await fetch(url, config)

            // 401 anywhere except login endpoints means the session died.
            if (response.status === 401
                && !endpoint.includes('/api/auth/login')) {
                this.handleUnauthorized()
            }

            const contentType = response.headers.get('content-type')
            if (contentType && contentType.includes('application/json')) {
                return await response.json()
            }
            const text = await response.text()
            return { success: response.ok, data: text }
        } catch (error) {
            console.error('API request failed:', error)
            throw error
        }
    }

    get(endpoint, options = {}) { return this.request('GET', endpoint, null, options) }
    post(endpoint, data, options = {}) { return this.request('POST', endpoint, data, options) }
    put(endpoint, data, options = {}) { return this.request('PUT', endpoint, data, options) }
    patch(endpoint, data, options = {}) { return this.request('PATCH', endpoint, data, options) }
    delete(endpoint, options = {}) { return this.request('DELETE', endpoint, null, options) }
}

export const api = new ApiService()

// -----------------------------------------------------------------------------
//  Shared helper for endpoints that return binary (PDF) bodies.
// -----------------------------------------------------------------------------
async function fetchPdfHelper(endpoint, fallbackName) {
    const stored = localStorage.getItem('ghasc_user')
    const token = stored ? JSON.parse(stored).token : null

    const response = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
        throw new Error(`PDF request failed: ${response.status}`)
    }

    const blob = await response.blob()
    const disp = response.headers.get('content-disposition') || ''
    const match = /filename="?([^"]+)"?/i.exec(disp)
    const fileName = match ? match[1] : fallbackName
    return { blob, fileName }
}

// -----------------------------------------------------------------------------
// Per-domain helpers — thin wrappers over `api` for clarity at the call site.
// -----------------------------------------------------------------------------

export const authApi = {
    login: (email, password) => api.post('/api/auth/login', { email, password }),

    logout: () => api.post('/api/auth/logout'),

    me: () => api.get('/api/auth/me'),

    forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),

    resetPassword: (token, newPassword) =>
        api.post('/api/auth/reset-password', { token, newPassword }),
}

export const healthApi = {
    ping: () => api.get('/api/health'),
}

export const customerApi = {
    list: (includeInactive = false) =>
        api.get(`/api/customers${includeInactive ? '?includeInactive=true' : ''}`),
    getById: (id) => api.get(`/api/customers/${id}`),
    create: (data) => api.post('/api/customers', data),
    update: (id, data) => api.put(`/api/customers/${id}`, data),
    deactivate: (id) => api.delete(`/api/customers/${id}`),
}

export const userApi = {
    list: (includeDisabled = false) =>
        api.get(`/api/users${includeDisabled ? '?includeDisabled=true' : ''}`),
    getById: (id) => api.get(`/api/users/${id}`),
    create: (data) => api.post('/api/users', data),
    update: (id, data) => api.put(`/api/users/${id}`, data),
    resetPassword: (id, newPassword) => api.post(`/api/users/${id}/reset-password`, { newPassword }),
    deactivate: (id) => api.delete(`/api/users/${id}`),
    deletePermanent: (id) => api.delete(`/api/users/${id}/permanent`),
}

export const signupApi = {
    submit: (data) => api.post('/api/signup', data),
    getByToken: (token) => api.get(`/api/signup/by-token/${token}`),
    listPending: () => api.get('/api/signup/pending'),
    approve: (token, body) => api.post(`/api/signup/${token}/approve`, body),
    reject: (token, body) => api.post(`/api/signup/${token}/reject`, body),
}

export const adminConfigApi = {
    get: () => api.get('/api/admin-config'),
    getPublic: () => api.get('/api/admin-config/public'),
    update: (data) => api.put('/api/admin-config', data),
    deleteLogo: () => api.delete('/api/admin-config/logo'),

    uploadLogo: async (file) => {
        const formData = new FormData()
        formData.append('file', file)

        const stored = localStorage.getItem('ghasc_user')
        const token = stored ? JSON.parse(stored).token : null

        const response = await fetch('/api/admin-config/logo', {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        })
        return response.json()
    },
}

export const inventoryApi = {
    list: (filters = {}) => {
        const params = new URLSearchParams()
        if (filters.category) params.set('category', filters.category)
        if (filters.q) params.set('q', filters.q)
        if (filters.isNew !== undefined && filters.isNew !== null) {
            params.set('isNew', String(filters.isNew))
        }
        const qs = params.toString()
        return api.get(`/api/inventory${qs ? `?${qs}` : ''}`)
    },
    listShop: (filters = {}) => {
        const params = new URLSearchParams()
        if (filters.category) params.set('category', filters.category)
        if (filters.q) params.set('q', filters.q)
        if (filters.isNew !== undefined && filters.isNew !== null) {
            params.set('isNew', String(filters.isNew))
        }
        const qs = params.toString()
        return api.get(`/api/inventory/shop${qs ? `?${qs}` : ''}`)
    },
    listCategories: () => api.get('/api/inventory/categories'),
    getById: (id) => api.get(`/api/inventory/${id}`),
    create: (data) => api.post('/api/inventory', data),
    update: (id, data) => api.put(`/api/inventory/${id}`, data),
    delete: (id) => api.delete(`/api/inventory/${id}`),

    uploadImage: async (id, file) => {
        const formData = new FormData()
        formData.append('file', file)

        const stored = localStorage.getItem('ghasc_user')
        const token = stored ? JSON.parse(stored).token : null

        const response = await fetch(`/api/inventory/${id}/image`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        })
        return response.json()
    },

    deleteImage: (id) => api.delete(`/api/inventory/${id}/image`),

    importCsv: async (file) => {
        const formData = new FormData()
        formData.append('file', file)

        const stored = localStorage.getItem('ghasc_user')
        const token = stored ? JSON.parse(stored).token : null

        const response = await fetch('/api/inventory/import-csv', {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        })
        return response.json()
    },

    /**
     * Download the current inventory as a CSV. Triggers a browser download.
     * Returns the parsed filename so the caller can show it in a toast.
     */
    exportCsv: async () => {
        const stored = localStorage.getItem('ghasc_user')
        const token = stored ? JSON.parse(stored).token : null

        const response = await fetch('/api/inventory/export-csv', {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!response.ok) {
            throw new Error(`Export failed: ${response.status}`)
        }

        const blob = await response.blob()
        const disp = response.headers.get('content-disposition') || ''
        const match = /filename="?([^"]+)"?/i.exec(disp)
        const fileName = match ? match[1] : `ghasc-inventory-${new Date().toISOString().slice(0, 10)}.csv`

        // Trigger browser download
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        return { success: true, fileName }
    },

    /**
     * Step 1 of the two-step import: parse + classify, no writes.
     * Returns { success, preview: { rows, totalRows, toCreate, toUpdate, errors } }.
     */
    previewImport: async (file) => {
        const formData = new FormData()
        formData.append('file', file)

        const stored = localStorage.getItem('ghasc_user')
        const token = stored ? JSON.parse(stored).token : null

        const response = await fetch('/api/inventory/import-csv/preview', {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        })
        return response.json()
    },

    /**
     * Step 2 of the two-step import: apply the rows the user kept.
     * `rows` is the array of CsvPreviewRow objects (skipped ones omitted).
     */
    commitImport: (rows) => api.post('/api/inventory/import-csv/commit', { rows }),
}

export const cartApi = {
    get: () => api.get('/api/cart'),
    add: (inventoryId, quantity) =>
        api.post('/api/cart', { inventoryId, quantity }),
    update: (cartItemId, quantity) =>
        api.put(`/api/cart/${cartItemId}`, { quantity }),
    remove: (cartItemId) => api.delete(`/api/cart/${cartItemId}`),
    clear: () => api.delete('/api/cart'),
}

export const areaPositionApi = {
    list: (includeInactive = false) =>
        api.get(`/api/area-positions${includeInactive ? '?includeInactive=true' : ''}`),
    create: (data) => api.post('/api/area-positions', data),
    update: (id, data) => api.put(`/api/area-positions/${id}`, data),
    deactivate: (id) => api.delete(`/api/area-positions/${id}`),
}

export const gsrReportApi = {
    list: () => api.get('/api/reports'),
    submitGsr: (data) => api.post('/api/reports/gsr', data),
    submitArea: (data) => api.post('/api/reports/area', data),

    fetchPdf: (id, { inline = false } = {}) => {
        const qs = inline ? '?inline=true' : ''
        return fetchPdfHelper(
            `/api/reports/${id}/pdf${qs}`,
            `Report-${id}.pdf`
        )
    },
}

export const medallionColorApi = {
    list: (includeInactive = false) =>
        api.get(`/api/medallion-colors${includeInactive ? '?includeInactive=true' : ''}`),
    create: (data) => api.post('/api/medallion-colors', data),
    update: (id, data) => api.put(`/api/medallion-colors/${id}`, data),
    deactivate: (id) => api.delete(`/api/medallion-colors/${id}`),
}

export const cycleApi = {
    current: () => api.get('/api/cycles/current'),
}

export const regionalOrderApi = {
    preview: () => api.get('/api/regional-orders/preview'),
    submit: (data) => api.post('/api/regional-orders', data),
    list: () => api.get('/api/regional-orders'),
    getById: (id) => api.get(`/api/regional-orders/${id}`),

    listOpenForReceiving: () => api.get('/api/regional-orders/open-receiving'),

    receive: (id, items, markDone = false) =>
        api.put(`/api/regional-orders/${id}/receive`, { items, markDone }),

    fetchPdf: (id, { inline = false } = {}) => {
        const qs = inline ? '?inline=true' : ''
        return fetchPdfHelper(
            `/api/regional-orders/${id}/pdf${qs}`,
            `Regional-Order-${id}.pdf`
        )
    },
}

export const orderApi = {
    preview: () => api.get('/api/orders/preview'),
    checkout: (data) => api.post('/api/orders/checkout', data),
    mine: (year, month) => {
        const params = new URLSearchParams()
        if (year) params.append('year', year)
        if (month) params.append('month', month)
        const qs = params.toString()
        return api.get(`/api/orders/mine${qs ? '?' + qs : ''}`)
    },
    listAll: (customerId, year, month) => {
        const params = new URLSearchParams()
        if (customerId) params.append('customerId', customerId)
        if (year) params.append('year', year)
        if (month) params.append('month', month)
        const qs = params.toString()
        return api.get(`/api/orders${qs ? '?' + qs : ''}`)
    },
    getById: (id) => api.get(`/api/orders/${id}`),
    cancel: (id, reason) => api.post(`/api/orders/${id}/cancel`, { reason }),
    updateStatus: (id, body) => api.put(`/api/orders/${id}/status`, body),

    fetchPdf: (id, { inline = false } = {}) => {
        const qs = inline ? '?inline=true' : ''
        return fetchPdfHelper(
            `/api/orders/${id}/pdf${qs}`,
            `Order-${id}.pdf`
        )
    },

    fetchPackingSlip: (id, { inline = false } = {}) => {
        const qs = inline ? '?inline=true' : ''
        return fetchPdfHelper(
            `/api/orders/${id}/packing-slip${qs}`,
            `PackingSlip-${id}.pdf`
        )
    },

    shortPack: (orderId, itemId, actualFilled) =>
        api.post(`/api/orders/${orderId}/items/${itemId}/short-pack`, { actualFilled }),
    listPendingBackorderSlips: () => api.get('/api/orders/backorders-ready'),
    fetchBackorderSlip: (orderId, batchId, { inline = false } = {}) => {
        const qs = inline ? '?inline=true' : ''
        return fetchPdfHelper(
            `/api/orders/${orderId}/backorder-slip/${batchId}/pdf${qs}`,
            `BackorderSlip-${orderId}-batch${batchId}.pdf`
        )
    },
    markBackorderSlipPrinted: (orderId, batchId) =>
        api.post(`/api/orders/${orderId}/backorder-slip/${batchId}/mark-printed`),
    listPendingBackorderSlipsForOrder: (orderId) =>
        api.get(`/api/orders/${orderId}/pending-backorder-slips`),
    listBackorderBatchItems: (orderId, batchId) =>
        api.get(`/api/orders/${orderId}/backorder-slip/${batchId}/items`),
}

export const chairReportsApi = {
    getMonthly: (cycle) => api.get(`/api/chair-reports/monthly?cycle=${encodeURIComponent(cycle)}`),
    getYearEnd: (year) => api.get(`/api/chair-reports/year-end?year=${year}`),

    fetchMonthlyPdf: (cycle, { inline = false } = {}) => {
        const qs = `cycle=${encodeURIComponent(cycle)}${inline ? '&inline=true' : ''}`
        return fetchPdfHelper(
            `/api/chair-reports/monthly/pdf?${qs}`,
            `Monthly-${cycle}.pdf`
        )
    },

    fetchYearEndPdf: (year, { inline = false } = {}) => {
        const qs = `year=${year}${inline ? '&inline=true' : ''}`
        return fetchPdfHelper(
            `/api/chair-reports/year-end/pdf?${qs}`,
            `YearEnd-${year}.pdf`
        )
    },
}

export const pastOrderApi = {
    /**
     * Upload PDFs and get back a preview of what would be imported.
     * customerId: number — the home group all PDFs belong to.
     * files: File[] — one or more PDF File objects from an <input type='file'>.
     */
    preview: async (customerId, files) => {
        const formData = new FormData()
        formData.append('customerId', String(customerId))
        for (const file of files) {
            formData.append('files', file)
        }

        const stored = localStorage.getItem('ghasc_user')
        const token = stored ? JSON.parse(stored).token : null

        const response = await fetch('/api/past-orders/preview', {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        })
        return response.json()
    },

    /**
     * Commit the rows the admin chose to import.
     * customerId: number
     * rows: PastOrderPreviewRow[] — exactly what the preview returned, with
     *       skipped rows omitted by the caller before sending.
     */
    commit: (customerId, rows, importMode) =>
        api.post('/api/past-orders/commit', { customerId, rows, importMode }),
}

export const starterPackageApi = {
    list: () => api.get('/api/starter-packages'),
    getById: (id) => api.get(`/api/starter-packages/${id}`),
    create: (data) => api.post('/api/starter-packages', data),
    update: (id, data) => api.put(`/api/starter-packages/${id}`, data),
    setActive: (id, isActive) => api.put(`/api/starter-packages/${id}/active`, { isActive }),
    delete: (id) => api.delete(`/api/starter-packages/${id}`),
    issue: (data) => api.post('/api/starter-packages/issue', data),
}